import { Box, Typography, Divider, styled } from '@mui/material';
import React from 'react';
import { formatPrice } from '../../utils/utils';
import RenderDetail from '../detail/renderDetail';
import { useTranslation } from 'next-i18next';

const StyledBodyTypo = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem !important',
  lineHeight: 1.4,
  color: theme.palette.common.black,
  [theme.breakpoints.down('lg')]: {
    fontSize: '1.6rem !important',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.35rem !important',
  },
}));

const InfoCurrencyRow = ({ label, value, currency }: { label: string; value: string; currency: string }) => (
  <Box display="flex" justifyContent="space-between" flexWrap="wrap" mb={3}>
    <StyledBodyTypo>{label}</StyledBodyTypo>
    <Box display="flex" gap={1.5} alignItems="center" justifyContent="flex-end">
      <StyledBodyTypo sx={{ color: 'primary.main', fontWeight: 500 }}>{value}</StyledBodyTypo>
      <StyledBodyTypo sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{currency}</StyledBodyTypo>
    </Box>
  </Box>
);

export const InfoRow = ({ icon, label, value, unit }: { icon?: any; label: string; value: any; unit?: string }) => {
  const { t } = useTranslation();
  const displayValue = value && value !== '-' ? value : t('not-specified');
  const showUnit = value && value !== '-' && value !== t('not-specified');

  return (
    <Box display="flex" alignItems="flex-start" gap={1} sx={{ height: '100%' }} mb={2}>
      {icon && (
        <Box
          sx={{
            minWidth: 36,
            mt: 0.3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => theme.palette.grey[500],
          }}
        >
          {icon}
        </Box>
      )}
      <Box flexGrow={1}>
        <Typography
          variant="body1"
          color="#8D8D8D"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, whiteSpace: 'normal' }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            color: '#575758',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Box component="span" color={(theme) => theme.palette.primary.main}>
            {displayValue}
          </Box>{' '}
          {showUnit ? unit : ''}
        </Typography>
      </Box>
    </Box>
  );
};

export const MainInfoRow = ({ icon, label, value, unit }: { icon?: any; label: string; value: any; unit?: string }) => {
  const { t } = useTranslation();
  const displayValue = value && value !== '-' ? value : t('not-specified');
  const showUnit = value && value !== '-' && value !== t('not-specified');

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} mb={1.5}>
      {icon && (
        <Box
          sx={{
            width: 42,
            height: 42,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => theme.palette.grey[500],
            bgcolor: '#F5F5F6',
            borderRadius: '50%',
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        variant="body2"
        align="center"
        sx={{ fontSize: { xs: 16, sm: 18 }, color: 'primary.main' }}
      >
        {displayValue} <Box component='span' sx={{ color: '#575758' }}>{showUnit ? unit : ''}</Box>
      </Typography>
      <Typography variant="body1" align="center" sx={{ fontSize: { xs: 14, sm: 16 } }}>
        {label}
      </Typography>
    </Box>
  );
};

export function InfoGrid({ children }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1, width: '100%' }}>
      {React.Children.map(children, (child) => (
        <Box
          sx={{
            flex: { xs: '1 1 50%', md: '1 1 33.333%' },
            maxWidth: { xs: '50%', md: '33.333%' },
            px: 1,
          }}
        >
          {child}
        </Box>
      ))}
    </Box>
  );
}

export const PropertyInfo = ({ propertyInfo, t, lang }) => {
  const getPrice = (value: any) => (Number(value) > 0 ? formatPrice(value, lang, false) : t('not-specified'));

  return (
    <Box>
      {/* Price Section */}
      <Box my={4}>
        {[
          {
            key: 'sell',
            value:
              Number(propertyInfo?.property_Price) > 0 ? propertyInfo?.property_Price : propertyInfo?.property_Sell,
            unit: t('THB'),
          },
          {
            key: 'rent',
            value: Number(propertyInfo?.Price_Rent) > 0 ? propertyInfo?.Price_Rent : undefined,
            unit: `${t('THB')}/${t('month')}`,
          },
          {
            key: 'mortgageOrRedemption',
            value:
              Number(propertyInfo?.price_mortgage) > 0 ? propertyInfo?.price_mortgage : propertyInfo?.price_redemption,
            unit: t('THB'),
          },
          {
            key: 'lease',
            value: Number(propertyInfo?.price_lease) > 0 ? propertyInfo?.price_lease : undefined,
            unit: t('THB'),
          },
        ].map((item, idx) => (
          <React.Fragment key={item.key}>
            <InfoCurrencyRow label={t(item.key)} value={getPrice(item.value)} currency={item.unit} />
            <Divider sx={{ mb: 2, borderColor: 'grey.100' }} />
          </React.Fragment>
        ))}
      </Box>

      {/* Property Details */}
      <Typography sx={{ mb: 2, fontSize: { xs: '1.4rem', md: '1.75rem' }, fontWeight: 500 }}>
        {t('property-detail')}
      </Typography>
      <RenderDetail data={propertyInfo} locale={lang} />
    </Box>
  );
};
